import { Field as FormField, FieldGroup, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { dropNulls } from '../../lib/api'
import type { ShipmentCorrectiveAction } from '../../lib/types'
import { answerLabel, ChoiceButtons, YesNo } from '../chips'
import { amountProblem, optNum, optText, str, type FlowDef, type StepCtx } from '../flow'
import { AmountInput, farmPath, line, TextField } from './shared'

// After harvest: packing and storing (Form Q) and shipping (Form O). Farm-level, like Forms G–M.

/** An attestation as sent: the answer, or left out when the grower didn't give one. */
const answer = (v: unknown) => (typeof v === 'boolean' ? v : null)
const answered = (v: unknown) => typeof v === 'boolean'

function YesNoField({ ctx, label, valueKey }: { ctx: StepCtx; label: string; valueKey: string }) {
  return (
    <YesNo label={label} value={ctx.values[valueKey]} onChange={(a) => ctx.patch({ [valueKey]: a })} />
  )
}

function QuantityField({ ctx, label }: { ctx: StepCtx; label: string }) {
  return (
    <div className="flex gap-3">
      <FormField className="flex-1">
        <Label>{label}</Label>
        <AmountInput value={ctx.values.quantity} onChange={(q) => ctx.patch({ quantity: q })} />
      </FormField>
      <FormField className="w-28">
        <Label>Unit</Label>
        <Input value={str(ctx.values.quantityUnit)} onChange={(e) => ctx.patch({ quantityUnit: e.target.value })} />
      </FormField>
    </div>
  )
}

const amount = (v: StepCtx['values']) => (optNum(v.quantity) == null ? [] : [{ label: 'Amount', value: `${str(v.quantity)} ${str(v.quantityUnit)}`.trim() }])
const hasAmount = (v: StepCtx['values']) => Number(str(v.quantity)) > 0 && !amountProblem(v.quantity) && str(v.quantityUnit).trim() !== ''

// --------------------------------------------------------------------------- Form Q

export const packingFlow: FlowDef = {
  kind: 'packing',
  checkForm: 'q',
  label: 'Packed or stored product',
  needsField: false,
  // Every attestation starts unanswered (see YesNo).
  initial: ({ today }) => ({ date: today, quantityUnit: 'flats' }),
  steps: [
    {
      id: 'what',
      title: 'What was packed or stored?',
      valid: (v) => str(v.productVariety).trim() !== '' && hasAmount(v),
      render: (ctx) => (
        <FieldGroup>
          <TextField ctx={ctx} label="Variety" valueKey="productVariety" />
          <QuantityField ctx={ctx} label="How much?" />
          <TextField ctx={ctx} label="Harvest date" valueKey="harvestedOn" type="date" />
          <TextField ctx={ctx} label="Field, bin or pallet tag" valueKey="fieldOrLotRef" />
          <TextField ctx={ctx} label="Incoming lot code" valueKey="incomingLotId" />
          <TextField ctx={ctx} label="Outgoing lot code" valueKey="outgoingLotId" />
          <TextField ctx={ctx} label="Who packed or stored it?" valueKey="handledByName" />
        </FieldGroup>
      ),
    },
    {
      id: 'packaging',
      title: 'Packaging and storage',
      valid: () => true,
      render: (ctx) => (
        <FieldGroup>
          <TextField ctx={ctx} label="Primary packaging" valueKey="primaryPackaging" />
          <TextField ctx={ctx} label="Secondary packaging" valueKey="secondaryPackaging" />
          <YesNoField ctx={ctx} label="Was the packaging material checked and OK?" valueKey="packagingChecked" />
          <TextField ctx={ctx} label="Put into storage on" valueKey="storedOn" type="date" />
          <YesNoField ctx={ctx} label="Is product rotated appropriately?" valueKey="rotatedAppropriately" />
        </FieldGroup>
      ),
    },
    {
      id: 'checks',
      title: 'Before it was packed',
      // Both are columns on Form Q: each needs an answer, never a default.
      valid: (v) => answered(v.phiMet) && answered(v.siteAssessed),
      render: (ctx) => (
        <FieldGroup>
          <YesNoField ctx={ctx} label="Were the waiting periods met? (Forms H1 and H2 checked)" valueKey="phiMet" />
          <YesNoField ctx={ctx} label="Was the production site assessed before harvest?" valueKey="siteAssessed" />
        </FieldGroup>
      ),
    },
  ],
  review: ({ values: v }) => ({
    title: `Packed · ${str(v.productVariety)}`,
    lines: [
      { label: 'Date', value: str(v.date) },
      ...amount(v),
      ...line('Harvested', v.harvestedOn),
      ...line('Field / tag', v.fieldOrLotRef),
      ...line('Incoming lot', v.incomingLotId),
      ...line('Outgoing lot', v.outgoingLotId),
      ...line('Packed by', v.handledByName),
      ...line('Primary packaging', v.primaryPackaging),
      ...line('Secondary packaging', v.secondaryPackaging),
      { label: 'Packaging checked', value: answerLabel(v.packagingChecked) },
      ...line('Stored on', v.storedOn),
      { label: 'Rotated', value: answerLabel(v.rotatedAppropriately) },
      { label: 'Waiting periods met', value: answerLabel(v.phiMet) },
      { label: 'Site assessed', value: answerLabel(v.siteAssessed) },
    ],
  }),
  entry: ({ values: v }) =>
    dropNulls({
      packedOn: str(v.date),
      productVariety: optText(v.productVariety) ?? null,
      quantity: optNum(v.quantity) ?? null,
      quantityUnit: optText(v.quantityUnit) ?? null,
      harvestedOn: optText(v.harvestedOn) ?? null,
      fieldOrLotRef: optText(v.fieldOrLotRef) ?? null,
      incomingLotId: optText(v.incomingLotId) ?? null,
      outgoingLotId: optText(v.outgoingLotId) ?? null,
      handledByName: optText(v.handledByName) ?? null,
      primaryPackaging: optText(v.primaryPackaging) ?? null,
      secondaryPackaging: optText(v.secondaryPackaging) ?? null,
      packagingChecked: answer(v.packagingChecked),
      storedOn: optText(v.storedOn) ?? null,
      rotatedAppropriately: answer(v.rotatedAppropriately),
      phiMet: answer(v.phiMet),
      siteAssessed: answer(v.siteAssessed),
    }),
  badge: { framework: 'CanadaGAP', formCode: 'Q', formName: 'Packing, Repacking, Storing and Brokerage of Market Product' },
  historyPath: farmPath('packing-records'),
}

// --------------------------------------------------------------------------- Form O

/** Form O corrective actions A–F. */
const CORRECTIVE_ACTIONS: { value: ShipmentCorrectiveAction; label: string }[] = [
  { value: 'refused_load', label: 'Refused to load' },
  { value: 'swept', label: 'Swept' },
  { value: 'rinsed', label: 'Rinsed' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'washed', label: 'Washed with soap' },
  { value: 'other', label: 'Other' },
]
const correctiveLabel = (v: unknown) => CORRECTIVE_ACTIONS.find((a) => a.value === v)?.label

export const shipmentFlow: FlowDef = {
  kind: 'shipment',
  checkForm: 'o',
  label: 'Shipped product',
  needsField: false,
  initial: ({ today }) => ({ date: today, quantityUnit: 'flats' }),
  steps: [
    {
      id: 'what',
      title: 'What went out, and where?',
      valid: (v) => str(v.productIdentifier).trim() !== '' && !amountProblem(v.quantity),
      render: (ctx) => (
        <FieldGroup>
          <TextField ctx={ctx} label="Lot code, or field / bin / pallet tag" valueKey="productIdentifier" />
          <QuantityField ctx={ctx} label="How much?" />
          <TextField ctx={ctx} label="Destination and customer" valueKey="destination" />
          <TextField ctx={ctx} label="Who loaded it?" valueKey="loaderName" />
        </FieldGroup>
      ),
    },
    {
      id: 'vehicle',
      title: 'The vehicle',
      valid: (v) => answered(v.vehicleInspected),
      render: (ctx) => (
        <FieldGroup>
          <TextField ctx={ctx} label="Truck or trailer ID" valueKey="truckId" />
          <YesNoField ctx={ctx} label="Was the vehicle inspected (pests, damage, odours, foreign material)?" valueKey="vehicleInspected" />
          <TextField ctx={ctx} label="Anything wrong with it? Leave blank if not." valueKey="vehicleIssue" multiline />
          {optText(ctx.values.vehicleIssue) && (
            <ChoiceButtons label="What was done about it?"
                value={str(ctx.values.correctiveAction) as ShipmentCorrectiveAction | ''}
                options={CORRECTIVE_ACTIONS}
                onChange={(a) => ctx.patch({ correctiveAction: a })}
              />
          )}
          <YesNoField ctx={ctx} label="Is product rotated appropriately?" valueKey="rotatedAppropriately" />
        </FieldGroup>
      ),
    },
  ],
  review: ({ values: v }) => ({
    title: `Shipped · ${str(v.productIdentifier)}`,
    lines: [
      { label: 'Date', value: str(v.date) },
      ...amount(v),
      ...line('To', v.destination),
      ...line('Loaded by', v.loaderName),
      ...line('Truck / trailer', v.truckId),
      { label: 'Vehicle inspected', value: answerLabel(v.vehicleInspected) },
      ...line('Problem found', v.vehicleIssue),
      ...(optText(v.vehicleIssue) ? [{ label: 'Done about it', value: correctiveLabel(v.correctiveAction) ?? 'Not answered' }] : []),
      { label: 'Rotated', value: answerLabel(v.rotatedAppropriately) },
    ],
  }),
  entry: ({ values: v }) => {
    const issue = optText(v.vehicleIssue)
    return dropNulls({
      shippedOn: str(v.date),
      productIdentifier: optText(v.productIdentifier) ?? null,
      quantity: optNum(v.quantity) ?? null,
      quantityUnit: optText(v.quantityUnit) ?? null,
      destination: optText(v.destination) ?? null,
      loaderName: optText(v.loaderName) ?? null,
      truckId: optText(v.truckId) ?? null,
      vehicleInspected: answer(v.vehicleInspected),
      vehicleIssue: issue ?? null,
      // A corrective action only means something for an issue that was found.
      correctiveAction: issue && correctiveLabel(v.correctiveAction) ? (v.correctiveAction as ShipmentCorrectiveAction) : null,
      rotatedAppropriately: answer(v.rotatedAppropriately),
    })
  },
  badge: { framework: 'CanadaGAP', formCode: 'O', formName: 'Transporting Product' },
  historyPath: farmPath('shipments'),
}
